import * as yup from "yup";
import { Form } from "@/components/organisms/Form";
import { request } from "@/common/api";
import { Buttons } from "@/components/atoms/Buttons";
import { useState } from "react";
import { showAlert } from "@/components/containers/Alert";
import { InputGroup } from "@/components/molecules/InputGroup";
import { useNavigate } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextControl } from "@/components/containers/Controls/TextControl";
import { BaseResponse } from "@/common/types/baseRespones";
import { FormInputBox } from "@/components/molecules/FormInputBox";
import { MainTemplate } from "@/components/templates/MainTemplate";
import { useRecoilValue } from "recoil";
import { loginTypeState } from "@/common/states/user";
import { Guardian, Student } from "@/common/types/student";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ReactComponent as IconArrow } from "@/styles/assets/svg/icon_arrow.svg";
import { GET_GUARDIAN, GET_STUDENT, GET_USER_INFO } from "@/common/querykeys";

const schema = yup.object({
  user: yup.object({
    userId: yup
      .string()
      .required("ID는 필수 입력입니다.")
      .matches(/^[a-zA-Z0-9]+$/, "ID는 영문 또는 숫자만 입력 가능합니다.")
      .min(4, "ID는 최소 4자 이상이어야 합니다.")
      .max(20, "ID는 최대 20자 이하이여야 합니다."),
  }),
});

export const UserIdEditPage = () => {
  const navigate = useNavigate();
  const loginType = useRecoilValue(loginTypeState);
  const [isIdCheck, setIsIdCheck] = useState(false);
  const { control, handleSubmit, getValues } = useForm({
    resolver: yupResolver(schema),
  });

  const { data: guardians } = useQuery<Guardian[]>({
    queryKey: [GET_GUARDIAN],
    queryFn: async () => {
      return await request<Guardian[]>({
        method: "GET",
        url: "/guardian",
      });
    },
  });

  const { data: students } = useQuery<Student[]>({
    queryKey: [GET_STUDENT],
    queryFn: async () => {
      return await request<Student[]>({
        method: "GET",
        url: "/student",
      });
    },
  });

  const { data: studentInfo } = useQuery<Student>({
    queryKey: [GET_USER_INFO],
    queryFn: async () => {
      return await request<Student>({
        method: "GET",
        url: "/student/info",
      });
    },
    enabled: loginType === "student",
  });

  const { data: guardianInfo } = useQuery<Guardian>({
    queryKey: [GET_USER_INFO],
    queryFn: async () => {
      return await request<Guardian>({
        method: "GET",
        url: "/guardian/info",
      });
    },
    enabled: loginType === "guardian",
  });

  const { mutateAsync: updateStudentId } = useMutation({
    mutationFn: (data: { id: string; newUserId: string }) => {
      return request<BaseResponse>({
        method: "PUT",
        url: "/student/edit-id",
        data: data,
      });
    },
  });

  const { mutateAsync: updateGuardianId } = useMutation({
    mutationFn: (data: { id: string; newUserId: string }) => {
      return request<BaseResponse>({
        method: "PUT",
        url: "/guardian/edit-id",
        data: data,
      });
    },
  });

  const onSubmit = async (data: any) => {
    try {
      if (loginType === "student") {
        if (studentInfo) {
          await updateStudentId({
            id: studentInfo.id,
            newUserId: data.user.userId,
          });
          navigate("/setting/user-editType");
          showAlert("변경 되었습니다.");
        }
      } else {
        if (guardianInfo) {
          await updateGuardianId({
            id: guardianInfo?.id,
            newUserId: data.user.userId,
          });
          navigate("/setting/user-editType");
          showAlert("변경 되었습니다.");
        }
      }
    } catch (error) {
      showAlert("잠시 후 다시 시도해주세요.");
    }
  };

  const handleClick = () => {
    const inputNewId = getValues();
    const isValidFormat = /^[a-zA-Z0-9]+$/.test(inputNewId.user.userId);

    if (inputNewId.user.userId) {
      if (inputNewId.user.userId.length === 0) {
        showAlert("ID를 입력하지 않았습니다. \n 입력 후 확인해 주세요.");
        setIsIdCheck(false);
      } else if (inputNewId.user.userId.length < 4) {
        showAlert("ID가 4자리 미만입니다. \n 다시 입력해 주세요.");
        setIsIdCheck(false);
      } else if (!isValidFormat) {
        showAlert(
          "ID는 영문 또는 숫자만 입력 가능합니다. \n 다시 입력해 주세요."
        );
        setIsIdCheck(false);
      } else {
        if (
          students?.find(
            (student) => student.userId === inputNewId.user.userId
          ) ||
          guardians?.find(
            (student) => student.userId === inputNewId.user.userId
          )
        ) {
          showAlert("이미 사용중인 ID 입니다.");
          setIsIdCheck(false);
        } else {
          showAlert("변경 가능한 ID 입니다.");
          setIsIdCheck(true);
        }
      }
    } else {
      showAlert("ID를 입력해주세요.");
      setIsIdCheck(false);
    }
  };

  return (
    <MainTemplate
      nav={false}
      subPage={true}
      menuBtn={false}
      buttonLeft={
        <Buttons
          type="button"
          size="headerTextIcon"
          layout="text"
          color="bright100"
          icon={<IconArrow />}
          label="뒤로"
          onClick={() => window.history.back()}
        />
      }
    >
      <Form
        title="아이디 변경"
        submitBtn={
          <Buttons
            type="submit"
            size="lg"
            color="primary100"
            layout="solid"
            label="변경"
            disabled={!isIdCheck}
            onClick={handleSubmit(onSubmit)}
          />
        }
        backBtn={
          <Buttons
            type="button"
            size="lg"
            color="primary100"
            layout="text"
            label="취소"
            onClick={() => window.history.back()}
          />
        }
      >
        <FormInputBox>
          <InputGroup direction="row" gridColumn="70% 30%">
            <>
              <Controller
                name="user.userId"
                control={control}
                render={({ field }) => (
                  <TextControl
                    control={control}
                    {...field}
                    name="user.userId"
                    label="아이디"
                    placeholder="아이디를 입력하세요."
                    required={true}
                  />
                )}
              />
              <Buttons
                type="button"
                size="form"
                layout="solid"
                color="primary100"
                label="ID 확인"
                onClick={handleClick}
              />
            </>
          </InputGroup>
        </FormInputBox>
      </Form>
    </MainTemplate>
  );
};
